import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import { quizProgressActions } from '../actions/quiz-progress.actions';
import {
    selectAnswers,
    selectIsFinished,
    selectIsPassed,
    selectProgress,
    selectProgressLoaded,
    selectProgressLoading,
    selectRetrying,
    selectSaving,
    selectSelectedOption,
} from '../selectors/quiz-progress.selectors';

@Injectable({
    providedIn: 'root',
})
export class QuizProgressFacade {
    private readonly store = inject(Store);

    // Observable selectors
    progress$ = (quizId: string) => this.store.select(selectProgress(quizId));
    progressNonNullable$ = (quizId: string) => this.progress$(quizId).pipe(filter((p) => p !== null));
    loading$ = (quizId: string) => this.store.select(selectProgressLoading(quizId));
    loaded$ = (quizId: string) => this.store.select(selectProgressLoaded(quizId));
    saving$ = (quizId: string) => this.store.select(selectSaving(quizId));
    retrying$ = (quizId: string) => this.store.select(selectRetrying(quizId));
    isFinished$ = (quizId: string) => this.store.select(selectIsFinished(quizId));
    isPassed$ = (quizId: string) => this.store.select(selectIsPassed(quizId));
    answers$ = (quizId: string) => this.store.select(selectAnswers(quizId));
    selectedOption$ = (quizId: string, questionId: string) => this.store.select(selectSelectedOption(quizId, questionId));

    // Signal selectors
    progress = (quizId: string) => this.store.selectSignal(selectProgress(quizId));
    loading = (quizId: string) => this.store.selectSignal(selectProgressLoading(quizId));
    loaded = (quizId: string) => this.store.selectSignal(selectProgressLoaded(quizId));
    saving = (quizId: string) => this.store.selectSignal(selectSaving(quizId));
    retrying = (quizId: string) => this.store.selectSignal(selectRetrying(quizId));
    isFinished = (quizId: string) => this.store.selectSignal(selectIsFinished(quizId));
    isPassed = (quizId: string) => this.store.selectSignal(selectIsPassed(quizId));
    answers = (quizId: string) => this.store.selectSignal(selectAnswers(quizId));
    selectedOption = (quizId: string, questionId: string) => this.store.selectSignal(selectSelectedOption(quizId, questionId));

    // Actions
    getProgress(quizId: string): void {
        this.store.dispatch(quizProgressActions.getProgress({ quizId }));
    }

    saveAnswer(quizId: string, questionId: string, optionId: number): void {
        this.store.dispatch(quizProgressActions.saveAnswer({ quizId, questionId, optionId }));
    }

    retry(quizId: string): void {
        this.store.dispatch(quizProgressActions.retry({ quizId }));
    }

    clear(quizId: string): void {
        this.store.dispatch(quizProgressActions.clear({ quizId }));
    }

    clearAll(): void {
        this.store.dispatch(quizProgressActions.clearAll());
    }
}
